import { SettingsNavButton } from "@/components/settings/settings-nav-button";
import { SettingsSectionLabel } from "@/components/settings/settings-section-label";
import { Toggle } from "@/components/toggle";
import ChangePasswordPage from "@/features/user-profile/pages/change-password";
import DeleteAccountPage from "@/features/user-profile/pages/delete-account";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { APP_ROUTES } from "@/constants/app-routes";
import { env } from "@/constants/env";
import { developerModeAtom } from "@/stores/developer-mode.store";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { useAtom } from "jotai";
import { useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { memo, useState } from "react";
import { createPortal } from "react-dom";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface AppSettingsPageProps {
  onBack?: () => void;
}

const pillCls =
  "bg-white border border-[#ebe0fb] rounded-[27px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] h-[52px] w-full flex items-center px-[18px]";

const AppSettingsPage = memo(function AppSettingsPage({ onBack }: AppSettingsPageProps) {
  const navigate = useIonRouter();
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  useCloseOnBackButton(showRestartConfirm, () => setShowRestartConfirm(false));
  const [isDeveloperModeEnabled, setIsDeveloperModeEnabled] = useAtom(developerModeAtom);
  const isNotProduction = env.VITE_ENVIRONMENT !== "production";

  const handleRestartOnboarding = () => {
    navigate.push(APP_ROUTES.ONBOARDING_START, "forward", "replace");
  };

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <SettingModalHeader title="App Settings" onBack={onBack} />

      <div className="flex flex-col gap-6 px-9 pt-4 pb-8">
        {/* Password */}
        <div className="flex flex-col gap-2">
          <SettingsSectionLabel label="Password" />
          <SettingsNavButton label="Change">
            {(onClose) => <ChangePasswordPage onBack={onClose} isModal />}
          </SettingsNavButton>
        </div>

        {/* Advanced Settings */}
        {isNotProduction && (
          <div className="flex flex-col gap-2">
            <SettingsSectionLabel label="Advanced Settings" />
            <div className={cn(pillCls, "justify-between")}>
              <span className={cn(textVariants.inputText, "text-black")}>Developer Mode</span>
              <Toggle
                checked={isDeveloperModeEnabled}
                onChange={setIsDeveloperModeEnabled}
                aria-label="Toggle developer mode for subscription debug view"
              />
            </div>
          </div>
        )}

        {/* Onboarding */}
        <div className="flex flex-col gap-2">
          <SettingsSectionLabel label="Onboarding" />
          <button
            type="button"
            onClick={() => setShowRestartConfirm(true)}
            className={cn(pillCls, "justify-center")}
          >
            <span className={cn(textVariants.inputText, "text-[#620ad5]")}>
              Restart Onboarding Process
            </span>
          </button>
        </div>

        {/* Account */}
        <div className="flex flex-col gap-2">
          <SettingsSectionLabel label="Account" />
          <SettingsNavButton label="Delete Account" labelClassName="text-red-600">
            {(onClose) => <DeleteAccountPage onBack={onClose} isModal />}
          </SettingsNavButton>
        </div>
      </div>

      {showRestartConfirm &&
        createPortal(
          <div className="animate-modal-fade-in fixed inset-0 z-[9999] flex items-center justify-center px-10">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowRestartConfirm(false)}
            />
            <div className="relative flex w-full max-w-[300px] flex-col items-center gap-2.5 rounded-[34px] bg-white px-3.5 py-3.5">
              <div className="space-y-2.5 px-2 pt-2 pb-6">
                <h2 className={cn(textVariants.title3, "text-center")}>
                  Restart onboarding process?
                </h2>
                <p className={cn(textVariants.body, "text-center")}>
                  Restarting onboarding will also reset your logged foods and the progress you made.
                </p>
              </div>
              <div className="w-full space-y-3">
                <FilledButton
                  color="black"
                  onClick={handleRestartOnboarding}
                  className="w-full uppercase"
                  style={{ backgroundColor: "#DC2626" }}
                >
                  Restart
                </FilledButton>
                <OutlinedButton
                  onClick={() => setShowRestartConfirm(false)}
                  color="gray"
                  textCase="normal"
                  className="w-full"
                >
                  Cancel
                </OutlinedButton>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
});

export default AppSettingsPage;
